/**
 * Loads all the accounts assigned to the user, allows the user to filter them and select one.
 */
class AccountSelectorContainer {
  /**
   * @param {ngQ}                   $q               To return a rejected promise if the contianer
   *                                                 tries to load the accounts more than once.
   * @param {FrontendConfiguration} appConfiguration To read the settings for the Photorank
   *                                                 accounts' avatars.
   * @param {AppErorHandler}        appErrorHandler  To handle the error if the request to get the
   *                                                 accounts fails.
   * @param {Auth}                  auth             The service that loads the accounts.
   */
  constructor($q, appConfiguration, appErrorHandler, auth) {
    'ngInject';

    /**
     * A local reference for the `$q` service.
     * @type {ngQ}
     */
    this.$q = $q;
    /**
     * A local reference for the app configuration.
     * @type {FrontendConfiguration}
     */
    this.appConfiguration = appConfiguration;
    /**
     * A local reference for the `appErrorHandler` service.
     * @type {appErrorHandler}
     */
    this.appErrorHandler = appErrorHandler;
    /**
     * A local reference for the `auth` service.
     * @type {Auth}
     */
    this.auth = auth;
    /**
     * A flag used to show the loading indicator while the accounts request is being made. This
     * will prevent the container from showing the child component until it needs the user input.
     * @type {boolean}
     */
    this.loading = false;
    /**
     * The information for user that is selecting an account. It will be obtained from the same
     * request that get the accounts list.
     * @type {Object}
     */
    this.user = {};
    /**
     * This will be the list of accounts assigned to an specific user.
     * @type {Array}
     */
    this.accounts = [];
    /**
     * This is the list that is sent to the child component. The reason we have both `accounts`
     * and `accountsList` is because `accounts` is the original list, and this is the one
     * filtered by the user search.
     * @type {Array}
     */
    this.accountsList = [];
    /**
     * The name of the implementation requesting the user to select an account. Once the accounts
     * are obtained, this will have the name of either an application or a permissions scope.
     * @type {string}
     */
    this.subtitle = '';
    /**
     * Whether or not the user can select an account. The flag will change to `false` after the
     * user makes a selection in order to prevent him/her from triggering the selection process
     * more than once.
     * @type {boolean}
     */
    this.canSelectAccount = true;
    /**
     * An error message to send the child component in case the accounts request fails.
     * @type {string}
     */
    this.error = '';
    /**
     * The query from the last search the user made. This allows the container to validate and
     * prevent filtering twice for the same query.
     * @type {string}
     * @access protected
     * @ignore
     */
    this._lastSearch = '';
    /**
     * This is generated when the container gets mounted and it's used to bust the cache of the
     * Photorank accounts' avatar URLs.
     * @type {String}
     * @access protected
     * @ignore
     */
    this._cacheControl = '';
  }
  /**
   * When the container gets mounted, it generates the cache control key for the Photorank
   * accounts' avatar URLs.
   */
  $onInit() {
    this._cacheControl = this._generateAvatarsCacheControl();
    this._loadContent();
  }
  /**
   * This is a callback sent to the child component and that allows the user to filter the
   * accounts list.
   * The method will try to match the query with the account name and Photorank ID.
   * @param {String} [text=''] The search query.
   */
  search(text = '') {
    const query = text.toLowerCase();
    if (query !== this._lastSearch) {
      this._lastSearch = query;
      if (query) {
        this.accountsList = this.accounts.filter((account) => {
          const { name, photorank_id: photorankId } = account;
          return name.toLowerCase().includes(query) ||
            photorankId.toString().toLowerCase().includes(query);
        });
      } else {
        this.accountsList = this.accounts.slice();
      }
    }
  }
  /**
   * This is a callback sent to the child component and that allows the user to select an
   * specific account. Once an account is selected, the parent container is informed.
   * @param {Object} account The selected account.
   */
  select(account) {
    if (this.canSelectAccount) {
      this.canSelectAccount = false;
      this.onSelection({
        account,
        canSwitchAccount: !!this.accounts.length,
      });
    }
  }
  /**
   * Loops a list of accounts in order to cut long names and insert their Photorank avatar.
   * @param {Array} accounts The list to update.
   * @return {Array}
   * @access protected
   * @ignore
   */
  _formatAccounts(accounts) {
    const { features: { avatars } } = this.appConfiguration;
    const limit = 90;
    const ellipsisLength = 3;
    const limitWithEllipsis = limit - ellipsisLength;
    return accounts.map((account) => Object.assign({
      avatar: `${avatars.url}/${account.photorank_id}.${avatars.format}?${this._cacheControl}`,
      title: (
        account.name.length >= limit ?
          `${account.name.substr(0, limitWithEllipsis)}...` :
          account.name
      ),
    }, account));
  }
  /**
   * The _"Cache control"_ is a timestamp used on the avatar URLs to prevent cache. The timestamp
   * doesn't include seconds as we need the image to be cached for at least one hour, that's also
   * the reason we don't use `getDate`.
   * @return {string}
   * @access protected
   * @ignore
   */
  _generateAvatarsCacheControl() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const hours = now.getHours();
    return `${year}${month}${day}${hours}`;
  }
  /**
   * Load and process the accounts.
   * @return {Promise<Object,Error>}
   * @access protected
   * @ignore
   */
  _loadContent() {
    let result;
    if (this.loading) {
      // Reject the returned promise if the request is already being made.
      result = this.$q.reject(new Error('The content is already being loaded'));
    } else {
      // Turn on the _"request flag"_.
      this.loading = true;
      // Set the variable that will have the preferred account information, if available.
      let preferredAccount = null;
      // Request the accounts using the binding properties.
      result = this.auth.getAccounts(this.token, this.permissionsScope)
      .then((response) => {
        // Save the user information.
        this.user = response.user;
        // Save the subtitle information
        if (response.application) {
          this.subtitle = response.application.title;
        } else if (response.scope) {
          this.subtitle = response.scope.title;
        }

        if (!response.accounts.length) {
          // If there are no accounts, inform it to the parent container.
          this.onEmptyAccounts();
        } else {
          this.accounts = this._formatAccounts(response.accounts);
          const preferredId = this.preferredId ? Number(this.preferredId) : 0;
          if (preferredId) {
            preferredAccount = this.accounts
            .find((account) => account.photorank_id === preferredId);
          }

          if (preferredAccount) {
            // If the preferred account is avaiable, select it.
            this.select(preferredAccount);
          } else if (this.accounts.length === 1) {
            // If there's only one account, select it.
            this.select(this.accounts[0]);
          } else {
            /**
             * If there are more than one account copy the accounts list. At this point,
             * there's no search query, so we don't need to filter anything.
             */
            this.accountsList = this.accounts.slice();
          }
        }
      })
      .catch((error) => {
        // The accounts request faild, inform with a generic error.
        this.error = 'There was an error loading the accounts, please try again';
        // Tell the error handler to handle the error without a notification.
        this.appErrorHandler.silent(error);
      })
      .then(() => {
        /**
         * If the accounts were loaded and there was only one account or a preferred account was
         * found, then the container already selected it. We shouldn't turn off the loading flag,
         * to prevent the user from seeing only one result before being automatically redirected.
         */
        if (this.accounts.length > 1 && !preferredAccount) {
          this.loading = false;
        }
      });
    }

    return result;
  }
}
/**
 * Show the screen where the user can filter and select a Photorank account.
 * @ngesdoc component
 * @ngname accountSelectorContainer
 * @ngcontroller {@link AccountSelectorContainer}
 * @ngcontainer
 * @ngmodule auth
 * @ngbinding {string}   token            The user authentication token.
 * @ngbinding {string}   permissionsScope The name of a permission scope to filter the accounts.
 * @ngbinding {number}   preferredId      The ID of a preferred customer account.
 * @ngbinding {Function} onSelection      The callback the container calls when the user selects
 *                                        an account. It receives `account` and
 *                                        `canSwitchAccount` parameters, with the information of
 *                                        the selected account and whether there are or not more
 *                                        accounts the user can switch to.
 * @ngbinding {Function} onEmptyAccounts  The callback the container calls when the user doesn't
 *                                        have at least one account assigned.
 * @ngbinding {Function} onSignout        The callback the container calls when the user wants to
 *                                        stop the authentication process and signout instead.
 */
export default {
  controller: AccountSelectorContainer,
  template: `
    <div
      ng-if="$ctrl.loading"
      class="odsSmallIndicator -fullWidth -fullHeight"
    ></div>
    <account-selector
      ng-if="!$ctrl.loading"
      subtitle="$ctrl.subtitle"
      error="$ctrl.error"
      user-name="$ctrl.user.name"
      accounts="$ctrl.accountsList"
      can-select="$ctrl.canSelectAccount"
      on-search="$ctrl.search(text)"
      on-selection="$ctrl.select(account)"
      on-signout="$ctrl.onSignout()"
    ></account-selector>
  `,
  bindings: {
    token: '@',
    permissionsScope: '@',
    preferredId: '@',
    onSelection: '&',
    onEmptyAccounts: '&',
    onSignout: '&',
  },
};
